<script lang="ts">
	import { untrack } from 'svelte'
	import { Button, ButtonSize, ButtonType, Modal } from '@repo/commons/components'
	import type { ReferenceTableRow } from '@repo/commons/models'
	import {
		toColumnEntries,
		toReferenceTableRow,
		toRowFieldValues,
		validateReferenceTableRow,
		type RowFieldValue
	} from '@repo/commons/utils'
	import type { ResourceSchema } from '@semanticedge/persistence-service-noheaders-client/src'
	import { t } from 'svelte-i18n'
	import ReferenceTableRowField from './ReferenceTableRowField.svelte'

	type Props = {
		visible: boolean
		schema?: ResourceSchema | undefined
		existingRow?: ReferenceTableRow | null
		editingIndex?: number | null
		isDuplicate?: boolean
		existingRows?: readonly ReferenceTableRow[]
		onSave: (row: ReferenceTableRow) => void
		onClose: VoidFunction
	}

	const {
		visible = false,
		schema,
		existingRow = null,
		editingIndex = null,
		isDuplicate = false,
		existingRows = [],
		onSave,
		onClose
	}: Props = $props()

	let errors = $state<Record<string, string>>({})

	const columnEntries = $derived(toColumnEntries(schema?.columns, schema?.columnOrder))

	let fieldValues: Record<string, RowFieldValue> = $derived(
		visible ? untrack(() => toRowFieldValues(columnEntries, existingRow)) : {}
	)

	const isEditing = $derived(editingIndex !== null)

	const modalTitle = $derived.by(() => {
		if (isEditing) return $t('ReferenceTableRowModal.editTitle')
		if (isDuplicate) return $t('ReferenceTableRowModal.duplicateTitle')

		return $t('ReferenceTableRowModal.addTitle')
	})

	const saveButtonText = $derived(
		isEditing ? $t('ReferenceTableRowModal.save') : $t('ReferenceTableRowModal.add')
	)

	const handleFieldChange = (columnName: string, value: RowFieldValue): void => {
		fieldValues = { ...fieldValues, [columnName]: value }
	}

	const handleClose = (): void => {
		errors = {}
		onClose()
	}

	const handleSave = (): void => {
		if (!schema) return

		const row = toReferenceTableRow(columnEntries, fieldValues)
		const validationErrors = validateReferenceTableRow(row, schema, existingRows, editingIndex)

		if (Object.keys(validationErrors).length > 0) {
			errors = validationErrors

			return
		}

		onSave(row)
		handleClose()
	}
</script>

<Modal visible={visible && !!schema} title={modalTitle} onclose={handleClose} scrollable={true}>
	{#snippet children()}
		<div class="form-fields">
			{#each columnEntries as [columnName, column] (columnName)}
				<ReferenceTableRowField
					{columnName}
					{column}
					value={fieldValues[columnName]}
					error={errors[columnName]}
					onChange={handleFieldChange}
				/>
			{/each}
		</div>
	{/snippet}

	{#snippet footer()}
		<div class="modal-footer-actions">
			<Button
				buttonText={saveButtonText}
				buttonType={ButtonType.BRAND}
				buttonSize={ButtonSize.LARGE}
				onclick={handleSave}
			/>
			<Button
				buttonText={$t('FORM.cancel')}
				buttonType={ButtonType.TERTIARY_INVERSE}
				buttonSize={ButtonSize.LARGE}
				onclick={handleClose}
			/>
		</div>
	{/snippet}
</Modal>

<style lang="postcss">
	.form-fields {
		@apply flex flex-col gap-4 min-w-[480px];
	}

	.modal-footer-actions {
		@apply flex gap-4;
	}
</style>
